import { FC, useState } from 'react'
import { useForm } from 'react-hook-form'

import CONFIG from '../../../config'
import { FormResponseStatus, IGender } from '../../models'
import { Button, FormFieldLoading, FormLabel, FormLabelError, Input, Radio, Select } from '../UI'
import { useAds } from '../../hooks'
import { adsService } from '../../services'

type AdminAdsFormProps = {
  type?: 'CREATE' | 'EDIT',
  ad?: any,
  onSubmitChange(respStatus: FormResponseStatus, message?: string): void
}

interface IAdsRegisterForm {
  image: string,
  url: string,
  fromAge: string,
  toAge: string,
  genderId: string,
  orderAdvertising: string,
  status: string
}

const AdminAdsForm:FC<AdminAdsFormProps> = ({ type = 'CREATE', ad, onSubmitChange }) => {
  const defaultValues: IAdsRegisterForm = {
    image: (ad && ad.image ? ad.image : ''),
    url: (ad && ad.url ? ad.url : ''),
    fromAge: (ad && ad.from_age ? ad.from_age : ''),
    toAge: (ad && ad.to_age ? ad.to_age : ''),
    genderId: (ad ? String(ad?.gender_id) : ''),
    orderAdvertising: (ad && ad.order_advertising ? ad.order_advertising : ''),
    status: (ad ? String(ad?.status) : 'true')
  }

  const { register, handleSubmit, formState: { errors }, setValue, reset } = useForm<IAdsRegisterForm>({
    defaultValues
  })
  const [submited, setSubmited] = useState<boolean>(false)
  const genders = useAds('GENDERS')

  const fieldErrorRemove = (field: any, value: string) => {
    setValue(field, value, {
      shouldValidate: true,
      shouldDirty: true
    })
  }

  const postForm = async (data: any) => {
    let resp: any

    if(type === 'CREATE') resp = await adsService.postAd(data)
    else if(type === 'EDIT') resp = await adsService.updateAd(data, ad.id)

    if(resp && resp.status === 'Ok') {
      if(type === 'CREATE') reset(defaultValues)
      setTimeout(() => onSubmitChange('SUCCESS'), 800)
    } else {
      setTimeout(() => onSubmitChange('ERROR', resp?.message), 800)
    }
    setSubmited(false)
  }

  const onSubmit = async (fData: IAdsRegisterForm) => {
    const {
      image,
      url,
      fromAge,
      toAge,
      genderId,
      orderAdvertising,
      status
    } = fData

    setSubmited(true)
    postForm({
      image,
      url,
      from_age: fromAge,
      to_age: toAge,
      gender_id: genderId,
      order_advertising: orderAdvertising,
      status: status === 'true'
    })
  }

  return (
    <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      <div className="row">
        <fieldset className="col-6">
          <FormLabel htmlFor="image" required>Imagen</FormLabel>
          <Input
            {...register('image', { required: 'Ingresa la URL de la imagen' })}
            id="image"
            name="image"
            placeholder="URL de la imagen"
            tabIndex={1}
            fullWidth
            error={!!errors.image}
            className="mt-5"
          />
          {errors.image && <FormLabelError>{errors.image?.message}</FormLabelError>}
        </fieldset>

        <fieldset className="col-6">
          <FormLabel htmlFor="url" required>URL del anuncio</FormLabel>
          <Input
            {...register('url', { required: 'Ingresa la URL del anuncio' })}
            id="url"
            name="url"
            placeholder="URL del anuncio"
            tabIndex={2}
            fullWidth
            error={!!errors.url}
            className="mt-5"
          />
          {errors.url && <FormLabelError>{errors.url?.message}</FormLabelError>}
        </fieldset>
      </div>

      <div className="row mt-30">
        <fieldset className="col-3">
          <FormLabel htmlFor="fromAge" required>Edad desde</FormLabel>
          <Input
            {...register('fromAge', {
                required: 'Digita Edad desde',
                pattern: {
                  value: CONFIG.FORM.PATTERN.NUMBER,
                  message: 'Digita solo números'
                }
              }
            )}
            id="fromAge"
            name="fromAge"
            placeholder="00"
            tabIndex={3}
            fullWidth
            maxLength={3}
            error={!!errors.fromAge}
            className="mt-5"
          />
          {errors.fromAge && <FormLabelError>{errors.fromAge?.message}</FormLabelError>}
        </fieldset>

        <fieldset className="col-3">
          <FormLabel htmlFor="toAge" required>Edad hasta</FormLabel>
          <Input
            {...register('toAge', {
                required: 'Digita Edad hasta',
                pattern: {
                  value: CONFIG.FORM.PATTERN.NUMBER,
                  message: 'Digita solo números'
                }
              }
            )}
            id="toAge"
            name="toAge"
            placeholder="00"
            tabIndex={4}
            fullWidth
            maxLength={3}
            error={!!errors.toAge}
            className="mt-5"
          />
          {errors.toAge && <FormLabelError>{errors.toAge?.message}</FormLabelError>}
        </fieldset>

        <fieldset className="col-3">
          <FormLabel htmlFor="orderAdvertising" required>Orden</FormLabel>
          <Input
            {...register('orderAdvertising', {
                required: 'Digita Orden',
                pattern: {
                  value: CONFIG.FORM.PATTERN.NUMBER,
                  message: 'Digita solo números'
                }
              }
            )}
            id="orderAdvertising"
            name="orderAdvertising"
            placeholder="00"
            tabIndex={5}
            fullWidth
            maxLength={4}
            error={!!errors.orderAdvertising}
            className="mt-5"
          />
          {errors.orderAdvertising && <FormLabelError>{errors.orderAdvertising?.message}</FormLabelError>}
        </fieldset>

        <fieldset className="col-3">
          <FormLabel htmlFor="status" required>Estado</FormLabel>
          <Select
            {...register('status', { required: 'Selecciona estado' })}
            id="status"
            name="status"
            defaultValue={defaultValues.status}
            tabIndex={6}
            fullWidth
            required
            error={!!errors.status}
            className="mt-5"
          >
            <option value="true">Habilitado</option>
            <option value="false">Deshabilitado</option>
          </Select>
          {errors.status && <FormLabelError>{errors.status?.message}</FormLabelError>}
        </fieldset>
      </div>

      <div className="row mt-30">
        <fieldset className="col-12">
          <FormLabel htmlFor="genderId" required>Género</FormLabel>
          <div className="position-relative mt-5 py-5">
            {genders.data ?
                <>
                  {genders.data.map((it: IGender, i: number) => {
                    return (
                      <div key={it.id} className={`d-inline-block${i > 0 ? ' ml-20' : ''}`}>
                        <Radio
                          {...register('genderId', { required: 'Selecciona Género' })}
                          id={`genderId${it.name}`}
                          name="genderId"
                          value={it.id}
                          tabIndex={i + 7}
                          error={!!errors.genderId}
                          onChange={() => fieldErrorRemove('genderId', String(it.id))}
                        />
                        <label htmlFor={`genderId${it.name}`}>{it.name}</label>
                      </div>
                    )
                  })}
                </>
              :
                <FormFieldLoading overlay />
            }
          </div>
          {errors.genderId && <FormLabelError>{errors.genderId?.message}</FormLabelError>}
        </fieldset>
      </div>

      <div className="register-form-btn-wrapper mt-40">
        <Button
          type="submit"
          tabIndex={8}
          fullWidth
          disabled={submited}
          loading={submited}
        >{type === 'CREATE' ? 'CREAR' : 'ACTUALIZAR'}</Button>
      </div>
    </form>
  )
}

export { AdminAdsForm }